import { useCallback, useEffect, useState } from 'react';
import { normalizeEndpoint } from '@/lib/normalize-endpoint';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseSearchResultsParams {
  pageSize?: number;
  autoFetch?: boolean;
  initialQuery?: string;
}

export const useSearchResults = <T = any>(
  searchEndpoint?: string,
  otherSearchEndpoint?: string,
  params?: UseSearchResultsParams
) => {
  const [searchResults, setSearchResults] = useState<T[]>([]);
  const [selectedSearchResult, setSelectedSearchResult] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const { entity } = useAuth();
  const { pageSize = 15, autoFetch = true, initialQuery = '' } = params || {};

  const fetchSearchResults = useCallback(
    async (
      endpoint: string | undefined,
      otherEndpoint: string | undefined,
      query = '',
      paginationParams?: { page?: number; pageSize?: number }
    ) => {
      if (!endpoint && !otherEndpoint) return [];

      setIsLoading(true);

      try {
        const url = otherEndpoint ? otherEndpoint : `entities/${entity?.slug}/erp/${normalizeEndpoint(endpoint)}`;
        const parseResponseData = (data: any): { items: T[]; total: number } => {
          if (Array.isArray(data)) {
            return { items: data as T[], total: data.length };
          }

          if (data && typeof data === 'object') {
            if (Array.isArray((data as any).results)) {
              return {
                items: (data as any).results as T[],
                total: (data as any).count ?? (data as any).results.length
              };
            }

            if (Array.isArray((data as any).data)) {
              return {
                items: (data as any).data as T[],
                total: (data as any).total ?? (data as any).count ?? (data as any).data.length
              };
            }

            if (Array.isArray((data as any).items)) {
              return {
                items: (data as any).items as T[],
                total: (data as any).total ?? (data as any).items.length
              };
            }
          }

          return { items: [] as T[], total: 0 };
        };

        const requestParams: Record<string, any> = {};

        if (paginationParams?.page !== undefined) {
          requestParams.page = paginationParams.page + 1;
        }
        if (paginationParams?.pageSize !== undefined) {
          requestParams.page_size = paginationParams.pageSize;
        }

        if (query) {
          requestParams.search = query;
        }

        const response = await api.get(url, { params: requestParams });

        if (response.status !== 200) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const { items, total } = parseResponseData(response.data);
        setSearchResults(items);
        setTotalItems(total);
        setSelectedSearchResult(null);
        return items;
      } catch (error) {
        setSearchResults([]);
        setTotalItems(0);
        return [] as T[];
      } finally {
        setIsLoading(false);
      }
    },
    [entity?.slug]
  );

  // Handle page change for pagination
  const handlePageChange = useCallback(
    async (page: number, searchQuery = '') => {
      setCurrentPage(page);
      await fetchSearchResults(searchEndpoint, otherSearchEndpoint, searchQuery, { page, pageSize });
    },
    [fetchSearchResults, searchEndpoint, otherSearchEndpoint, pageSize]
  );

  useEffect(() => {
    if (!autoFetch) return;

    if (otherSearchEndpoint || searchEndpoint) {
      fetchSearchResults(searchEndpoint, otherSearchEndpoint, initialQuery, { page: currentPage, pageSize });
    }
  }, [autoFetch, otherSearchEndpoint, searchEndpoint, fetchSearchResults, currentPage, pageSize, initialQuery]);

  return {
    searchResults,
    setSearchResults,
    selectedSearchResult,
    setSelectedSearchResult,
    fetchSearchResults,
    isLoading,
    totalItems,
    currentPage,
    handlePageChange
  } as const;
};
